import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Drawer, Form, Input, Spin, TreeSelect } from "antd";
import PropTypes from "prop-types";

import { Modals, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

import Upload from "components/Upload";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const Component = ({ data = {}, onUpdate, options: { categories = [] } }) => {
	const initialState = {};
	const [state, setState] = useState(initialState);
	const { submitting, visible } = state;
	const { slug, title } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleClose = () => {
		navigate(-1);
	};

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));
				values.iconId = values.icon?.id || 109;
				values.parentId = values.parentId || null;

				values = ObjectNumbersToEnglish(values);

				delete values.icon;

				const action =
					hash === `#${Modals.CREATE}`
						? API.category.create(values)
						: API.category.update(slug, values);

				action
					.then(() => {
						onUpdate(null, true);
						handleClose();
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (slug) {
					setState((prevState) => ({ ...prevState, visible: true }));

					form.setFieldsValue(data);
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	const footer = (
		<>
			<Button
				type="primary"
				onClick={handleSubmit}
				loading={submitting}
			>
				{Resources.confirm}
			</Button>
			<Button
				onClick={() => handleClose()}
				loading={submitting}
			>
				{Resources.cancel}
			</Button>
		</>
	);

	return (
		<Drawer
			className="sp-form-drawer"
			footer={footer}
			onClose={() => handleClose()}
			title={
				slug
					? `${Resources.edit} ${Resources.category} "${title || "-"}"`
					: `${Resources.add} ${Resources.category}`
			}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form
				layout="vertical"
				form={form}
			>
				<Form.Item
					name="title"
					label={Resources.title}
					rules={Rules.required}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="parentId"
					label={Resources.category}
				>
					<TreeSelect
						treeData={categories}
						treeNodeFilterProp="label"
						showSearch
						allowClear
					/>
				</Form.Item>
				<Form.Item
					name="icon"
					label={Resources.image}
				>
					<Upload section={Types.upload.category} />
				</Form.Item>
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
};

export default Component;
